import {
  settlementBridgeDeckManifest,
  type SettlementIslandBridge,
} from './settlement-island-bridges';
import type { IslandPoint, IslandQuad } from './settlement-island-registration';

export interface SettlementBridgeMaterialPlan {
  timber: { source: IslandQuad; target: IslandQuad }[];
  landings: IslandQuad[];
}

const MAX_TIMBER_SLICES = 16;
const ENDCAP_TEXELS = 100;
const LANDING_COLUMNS = 3;

function finitePoint(point: IslandPoint | undefined): point is IslandPoint {
  return !!point && Number.isFinite(point.x) && Number.isFinite(point.y);
}

function validQuad(quad: IslandQuad): boolean {
  if (!Array.isArray(quad) || quad.length !== 4) return false;
  if (![quad[0], quad[1], quad[2], quad[3]].every(finitePoint)) return false;
  const along = { x: quad[1].x - quad[0].x, y: quad[1].y - quad[0].y };
  const across = { x: quad[3].x - quad[0].x, y: quad[3].y - quad[0].y };
  const determinant = along.x * across.y - along.y * across.x;
  const tolerance = 1e-7 * Math.max(1, Math.hypot(along.x, along.y, across.x, across.y));
  return (
    Number.isFinite(determinant) &&
    determinant !== 0 &&
    Math.abs(quad[2].x - quad[1].x - across.x) <= tolerance &&
    Math.abs(quad[2].y - quad[1].y - across.y) <= tolerance
  );
}

function interpolate(a: IslandPoint, b: IslandPoint, t: number): IslandPoint {
  // Preserve exact shared contacts at the perimeter as well as between adjacent slices.
  if (t === 0) return { ...a };
  if (t === 1) return { ...b };
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

function section(quad: IslandQuad, start: number, end: number): IslandQuad {
  return [
    interpolate(quad[0], quad[1], start),
    interpolate(quad[0], quad[1], end),
    interpolate(quad[3], quad[2], end),
    interpolate(quad[3], quad[2], start),
  ];
}

/**
 * Flat material only. The caller owns shore review, unlocks and the registered deck footprint.
 * Ground distance (not projected screen length) fixes texel density across headings and zoom.
 * No support height, territory, collision geometry or gameplay state is inferred here.
 */
export function planSettlementBridgeMaterial(
  bridge: SettlementIslandBridge,
): SettlementBridgeMaterialPlan {
  const empty = (): SettlementBridgeMaterialPlan => ({ timber: [], landings: [] });
  if (
    !bridge ||
    !Array.isArray(bridge.sourceEndpoints) ||
    bridge.sourceEndpoints.length !== 2 ||
    !finitePoint(bridge.sourceEndpoints[0]) ||
    !finitePoint(bridge.sourceEndpoints[1]) ||
    !Number.isFinite(bridge.groundWidthCells) ||
    bridge.groundWidthCells !== 22 / 32 ||
    !validQuad(bridge.deckQuad)
  )
    return empty();

  const [start, end] = bridge.sourceEndpoints;
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const groundLength = Math.hypot((dx / 32 + dy / 11) / 2, (dy / 11 - dx / 32) / 2);
  const source = settlementBridgeDeckManifest.sourceQuad;
  const texelsPerCell = (source[3].y - source[0].y) / bridge.groundWidthCells;
  const endcapCells = ENDCAP_TEXELS / texelsPerCell;
  const repeatTexels = source[1].x - source[0].x - 2 * ENDCAP_TEXELS;
  const repeatCells = repeatTexels / texelsPerCell;
  const middleCells = groundLength - 2 * endcapCells;
  const middleCount = Math.ceil(middleCells / repeatCells);
  const landingCells = bridge.groundWidthCells / LANDING_COLUMNS;
  if (
    !Number.isFinite(groundLength) ||
    groundLength < 2 * landingCells ||
    middleCount < 1 ||
    middleCount > MAX_TIMBER_SLICES - 2
  )
    return empty();

  const timber: SettlementBridgeMaterialPlan['timber'] = [];
  const addTimber = (sourceStart: number, texels: number, from: number, to: number) => {
    timber.push({
      source: [
        { x: sourceStart, y: source[0].y },
        { x: sourceStart + texels, y: source[0].y },
        { x: sourceStart + texels, y: source[3].y },
        { x: sourceStart, y: source[3].y },
      ],
      target: section(bridge.deckQuad, from, to),
    });
  };
  const capFraction = endcapCells / groundLength;
  addTimber(source[0].x, ENDCAP_TEXELS, 0, capFraction);
  let from = capFraction;
  for (let index = 0; index < middleCount; index++) {
    const cells = Math.min(repeatCells, middleCells - index * repeatCells);
    const to =
      index === middleCount - 1
        ? 1 - capFraction
        : (endcapCells + (index + 1) * repeatCells) / groundLength;
    addTimber(source[0].x + ENDCAP_TEXELS, cells * texelsPerCell, from, to);
    from = to;
  }
  addTimber(source[1].x - ENDCAP_TEXELS, ENDCAP_TEXELS, from, 1);

  // Three square ground patches across each end avoid stretching square limestone art.
  const landings: IslandQuad[] = [];
  const landingFraction = landingCells / groundLength;
  for (const [from, to] of [
    [0, landingFraction],
    [1 - landingFraction, 1],
  ] as const) {
    const endSection = section(bridge.deckQuad, from, to);
    for (let column = 0; column < LANDING_COLUMNS; column++) {
      const left = column / LANDING_COLUMNS;
      const right = (column + 1) / LANDING_COLUMNS;
      landings.push([
        interpolate(endSection[0], endSection[3], left),
        interpolate(endSection[1], endSection[2], left),
        interpolate(endSection[1], endSection[2], right),
        interpolate(endSection[0], endSection[3], right),
      ]);
    }
  }
  if (![...timber.map(({ target }) => target), ...landings].every(validQuad)) return empty();
  return { timber, landings };
}
